import { useEffect, useState } from "react";
import { Box, Title, Text, Select, Textarea, Group, Button, Modal } from "@mantine/core";
import { IconCheck, IconX } from '@tabler/icons';
import { useForm } from "@mantine/form";
import { saveIdea } from '../../../Api';
import { useAuth0 } from '@auth0/auth0-react';
import { showNotification } from '@mantine/notifications';
import { useCurrentUser } from "../../../hooks/useCurrentUser";

interface ArchiveIdeaModalProps {
  opened: boolean;
  onClose: () => void;
  idea: any;
  onSuccess?: () => void;
}

export function ArchiveIdeaModal({ opened, onClose, idea, onSuccess }: ArchiveIdeaModalProps) {
  const [loading, setLoading] = useState(false);
  
  const form = useForm({
    initialValues: {
      reason: "",
      comment: "",
    },
    validate: {
      reason: (value: string) => (value ? null : "Reason is required"),
      comment: (value: string) => {
        if (!value) {
          return "Comment is required";
        }
        if (value.length < 5) {
          return "Comment must be at least 5 characters";
        }
        return null;
      },
    },
  });

  const auth0 = useAuth0();
  const currentUser = useCurrentUser();

  const handleArchive = async (values: any) => {
    setLoading(true);
    try {
      const isArchived = idea.state === "archived";

      const generatedRejectInfo: any = {
        reason: values.reason,
        comment: values.comment,
        rejectingUserId: currentUser?.id,
        rejectedAt: new Date().toISOString()
      };

      const request = {
        id: idea?._id,
        state: "archived",
        generatedRejectInfo
      };

      await saveIdea(request, auth0);
      showNotification({
        title: isArchived ? "The AI-generated idea has been moved to actual state of idea" : "The AI-generated idea has been moved to archive",
        color: "teal",
        message: <Text
          onClick={() => {
            request.state = isArchived ? "archived" : "generated";
            request.generatedRejectInfo = {};

            saveIdea(request, auth0)
              .then(() => {            
                showNotification({
                  id: 'idea-archive',
                  title: !isArchived ? "The AI-generated idea has been moved to actual state of idea" : "The AI-generated idea has been moved to archive",
                  message: `The AI-generated idea has been restored to ${idea.state}`,
                  color: "teal",
                  autoClose: 3000,
                  icon: <IconCheck size="1.1rem" />,
                });
              })
              .catch((e) => console.error("Undo failed", e));
          }}
          style={{
            color: "#228be6",
            cursor: "pointer",
            fontWeight: 500,
            display: "inline-block",
            marginTop: 8,
          }}
        >
                Undo
        </Text>,
        icon: <IconCheck size="1.1rem" />,
        autoClose: 3000
      });
     

      onClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Failed to archive idea:", error);
      showNotification({
        title: "Error",
        color: "red",
        message: "Failed to archive the idea. Please try again.",
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };


  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      form.reset();
    }
  }, [opened]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="md"
      centered
      withCloseButton={false}
      styles={{
        content: {
          borderRadius: '12px',
        },
        header: {
          borderBottom: 'none',
        },
        body: {
          padding: '24px',
        },
      }}
    >
      <Box>
        {/* Header with close button */}
        <Group position="apart" mb="md">
          <Text size="sm" fw={500}>
            Move this Idea to Archive page
          </Text>
          <Button
            variant="subtle"
            size="xs"
            onClick={onClose}
            sx={{
              color: '#6C757D',
              '&:hover': {
                backgroundColor: '#F8F9FA',
              },
            }}
          >
            <IconX size={16} />
          </Button>
        </Group>

        {/* Main title */}
        <Title 
          order={3} 
          color="#5B60E3" 
          align="left" 
          mb="xl"
          sx={{ 
            fontSize: '20px',
            fontWeight: 600,
            lineHeight: 1.3
          }}
        >
          Let us know why this AI-Generated Idea is not a good fit
        </Title>

        {/* Form */}
        <form onSubmit={form.onSubmit(handleArchive)}>
          <Select
            mb="md"
            label="Reason"
            placeholder="Select reason"
            withAsterisk
            data={[
              "Too general",
              "Not related to a product capability", 
              "Wrong product area/domain",
              "Too similar to an existing Idea",
              "Other reason",
            ]}
            styles={{
              input: {
                height: '40px',
                border: '1px solid #E9ECEF',
                borderRadius: '6px',
                fontSize: '14px',
              },
              label: {
                fontSize: '14px',
                fontWeight: 500,
                color: '#212529',
                marginBottom: '8px',
              },
            }}
            {...form.getInputProps("reason")}
          />

          <Textarea
            withAsterisk
            placeholder="Add your comment"
            label="Add comment"
            minRows={3}
            styles={{
              input: {
                border: '1px solid #E9ECEF',
                borderRadius: '6px',
                fontSize: '14px',
              },
              label: {
                fontSize: '14px',
                fontWeight: 500,
                color: '#212529',
                marginBottom: '8px',
              },
            }}
            {...form.getInputProps("comment")}
          />

          <Group position="center" mt="xl">
            <Button
              type="submit"
              loading={loading}
              disabled={loading}
              sx={{
                backgroundColor: "#5B60E3",
                "&:hover": { 
                  backgroundColor: "#4c50ba" 
                },
                "&:disabled": {
                  backgroundColor: "#E9ECEF",
                  color: "#6C757D"
                },
                fontWeight: 500,
                fontSize: 14,
                height: '40px',
                padding: '0 24px',
                borderRadius: '6px',
              }}
            >
              Archive
            </Button>
          </Group>
        </form>
      </Box>
    </Modal>
  );
}
